import { Controller } from "@hotwired/stimulus"

// macOS / iOS "Liquid Glass"-like toast motion:
// - fast, soft spring-in with a tiny overshoot
// - gentle lift + blur on exit
// Uses Web Animations API for reliable overshoot (cubic-bezier overshoot can be clamped on iOS).

export default class extends Controller {
	connect() {
		this._reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches

		// Enter
		this.enterAnimation = this.animateEnter()

		// Auto-dismiss
		this.timeout = setTimeout(() => {
			this.remove()
		}, 8000)
	}

	disconnect() {
		clearTimeout(this.timeout)
		this.enterAnimation?.cancel()
		this.leaveAnimation?.cancel()
	}

	remove() {
		clearTimeout(this.timeout)

		// If we're already leaving, don't start another leave animation
		if (this.leaving) return
		this.leaving = true

		this.leaveAnimation = this.animateLeave()
		this.leaveAnimation.finished
			.catch(() => {})
			.then(() => {
				this.element.remove()
			})
	}

	animateEnter() {
		if (this._reducedMotion) {
			this.element.style.opacity = "1"
			this.element.style.transform = "translate3d(0,0,0) scale(1)"
			this.element.style.filter = "blur(0px)"
			return null
		}

		// Two-stage spring: overshoot then settle.
		// Values tuned to feel close to Apple system toasts.
		const keyframes = [
			{
				opacity: 0,
				transform: "translate3d(0, 14px, 0) scale(0.92)",
				filter: "blur(10px)",
			},
			{
				opacity: 1,
				transform: "translate3d(0, 0px, 0) scale(1.02)",
				filter: "blur(0px)",
				offset: 0.72,
			},
			{
				opacity: 1,
				transform: "translate3d(0, 0px, 0) scale(1)",
				filter: "blur(0px)",
			},
		]

		return this.element.animate(keyframes, {
			duration: 420,
			easing: "cubic-bezier(0.16, 1, 0.3, 1)", // snappy ease-out (Apple-like)
			fill: "forwards",
		})
	}

	animateLeave() {
		if (this._reducedMotion) {
			return this.element.animate([{ opacity: 1 }, { opacity: 0 }], {
				duration: 150,
				easing: "ease-in",
				fill: "forwards",
			})
		}

		const keyframes = [
			{
				opacity: 1,
				transform: "translate3d(0, 0px, 0) scale(1)",
				filter: "blur(0px)",
			},
			{
				opacity: 0,
				transform: "translate3d(0, -10px, 0) scale(0.98)",
				filter: "blur(8px)",
			},
		]

		return this.element.animate(keyframes, {
			duration: 240,
			easing: "cubic-bezier(0.4, 0, 1, 1)",
			fill: "forwards",
		})
	}
}
